import { NgModule } from '@angular/core';

import { PRODUCT_COMMAND_TOKEN, PROFILE_COMMAND_TOKEN } from '@core/services/commands/command-manager.types';

import { BlockUserCommand } from '@pages/other/profile/_commands/block-user.command';
import { EnableBlockedProductsCommand } from '@pages/other/profile/_commands/enable-blocked-products.command';
import { EnableFingerprintCommand } from '@pages/other/profile/_commands/enable-fingerprint.command';
import { EnableNotificationCommand } from '@pages/other/profile/_commands/enable-notification.command';
import { NotificationsSettingsCommand } from '@pages/other/profile/_commands/notifications-settings.command';
import { PasswordChangeCommand } from '@pages/other/profile/_commands/password-change.command';
import { ResetPinCommand } from '@pages/other/profile/_commands/reset-pin.command';
import { ShowHiddenProductsCommand } from '@pages/other/profile/_commands/show-hidden-products.command';
import { TransferAndPaymentsCommand } from '@pages/other/profile/_commands/transfer-and-payments.command';
import { ApplicationOfOpeningAccountCommand } from '@pages/product/_commands/account/application-of-opening-account.command';

import {
  AboutCommand,
  AdditionalCardCommand,
  ArrestCommand,
  B2pCommand,
  BuyMetalCommand,
  CardGroupCommand,
  CardNotificationsCommand,
  ChangePinCodeCommand,
  CreditDocsCommand,
  CreditPaymentCommand,
  CreditReferencesCommand,
  DebitFromDepositCommand,
  DepositAgreementCommand,
  DisableParameterChangesCommand,
  EarlyRepaymentCommand,
  HideAccountCommand,
  HideStatementsCommand,
  LimitsCommand,
  LockCardCommand,
  MortgageInsuranceCommand,
  PaymentOrTransferCommand,
  PaymentScheduleCommand,
  ProductCloseCommand,
  RenameCommand,
  ReopenCardCommand,
  ReplenishCommand,
  RequisitesCardCommand,
  RequisitesCommand,
  RestrictAccountCommand,
  RestrictionsCommand,
  SaleMetalCommand,
  SetSpendingLimitCommand,
  StatementCommand,
  ThreeDSecureCommand,
  VirtualCardCommand,
  VirtualCvvCommand,
  VirtualPinCommand,
  WithdrawCommand
} from '../pages/product/_commands';
import { SalaryOrPensionTransferRedirectCommand } from '../pages/product/_commands/card/salary-or-pension-transfer-redirect.command';
import { RESTRICTION_COMMAND_TOKEN } from './services/commands/command-manager.types';

/**
 * Модуль для глобальной регистрации команд в приложении
 * Должен быть импортирован в AppModule для корректной работы CommandManagerService
 */
@NgModule({
  providers: [
    // Регистрация профильных команд
    TransferAndPaymentsCommand,
    ResetPinCommand,
    PasswordChangeCommand,
    BlockUserCommand,
    EnableBlockedProductsCommand,
    ShowHiddenProductsCommand,
    EnableNotificationCommand,
    EnableFingerprintCommand,
    NotificationsSettingsCommand,

    // Токен для CommandManagerService на глобальном уровне
    {
      provide: PROFILE_COMMAND_TOKEN,
      useFactory: (
        transferCmd: TransferAndPaymentsCommand,
        resetPinCmd: ResetPinCommand,
        passwordCmd: PasswordChangeCommand,
        blockUserCmd: BlockUserCommand,
        blockedProductsCmd: EnableBlockedProductsCommand,
        hiddenProductsCmd: ShowHiddenProductsCommand,
        notificationCmd: EnableNotificationCommand,
        fingerprintCmd: EnableFingerprintCommand,
        notificationsSettingsCmd: NotificationsSettingsCommand
      ) => [
        transferCmd,
        resetPinCmd,
        passwordCmd,
        blockUserCmd,
        blockedProductsCmd,
        hiddenProductsCmd,
        notificationCmd,
        fingerprintCmd,
        notificationsSettingsCmd
      ],
      deps: [
        TransferAndPaymentsCommand,
        ResetPinCommand,
        PasswordChangeCommand,
        BlockUserCommand,
        EnableBlockedProductsCommand,
        ShowHiddenProductsCommand,
        EnableNotificationCommand,
        EnableFingerprintCommand,
        NotificationsSettingsCommand
      ]
    },
    {
      provide: RESTRICTION_COMMAND_TOKEN,
      useFactory: (
        hideAccountCmd: HideAccountCommand,
        setSpendingLimitCmd: SetSpendingLimitCommand,
        hideStatementsCmd: HideStatementsCommand,
        disableParameterChangesCmd: DisableParameterChangesCommand,
        restrictAccountCmd: RestrictAccountCommand
      ) => [hideAccountCmd, setSpendingLimitCmd, hideStatementsCmd, disableParameterChangesCmd, restrictAccountCmd],
      deps: [
        HideAccountCommand,
        SetSpendingLimitCommand,
        HideStatementsCommand,
        DisableParameterChangesCommand,
        RestrictAccountCommand
      ]
    },
    {
      provide: PRODUCT_COMMAND_TOKEN,
      useFactory: (
        aboutCmd: AboutCommand,
        additionalCardCmd: AdditionalCardCommand,
        arrestCmd: ArrestCommand,
        b2pCmd: B2pCommand,
        buyMetalCmd: BuyMetalCommand,
        cardGroupCmd: CardGroupCommand,
        cardNotificationsCmd: CardNotificationsCommand,
        changePinCodeCmd: ChangePinCodeCommand,
        creditDocsCmd: CreditDocsCommand,
        creditPaymentCmd: CreditPaymentCommand,
        creditReferencesCmd: CreditReferencesCommand,
        debitFromDepositCmd: DebitFromDepositCommand,
        depositAgreementCmd: DepositAgreementCommand,
        earlyRepaymentCmd: EarlyRepaymentCommand,
        limitsCmd: LimitsCommand,
        lockCardCmd: LockCardCommand,
        mortgageInsuranceCmd: MortgageInsuranceCommand,
        paymentOrTransferCmd: PaymentOrTransferCommand,
        paymentScheduleCmd: PaymentScheduleCommand,
        productCloseCmd: ProductCloseCommand,
        renameCmd: RenameCommand,
        reopenCardCmd: ReopenCardCommand,
        replenishCmd: ReplenishCommand,
        requisitesCardCmd: RequisitesCardCommand,
        requisitesCmd: RequisitesCommand,
        restrictionsCmd: RestrictionsCommand,
        salaryOrPensionTransferRedirectCmd: SalaryOrPensionTransferRedirectCommand,
        saleMetalCmd: SaleMetalCommand,
        statementCmd: StatementCommand,
        threeDSecureCmd: ThreeDSecureCommand,
        virtualCardCmd: VirtualCardCommand,
        virtualCvvCmd: VirtualCvvCommand,
        virtualPinCmd: VirtualPinCommand,
        withdrawCmd: WithdrawCommand,
        applicationOfOpeningAccountCmd: ApplicationOfOpeningAccountCommand
      ) => [
        aboutCmd,
        additionalCardCmd,
        arrestCmd,
        b2pCmd,
        buyMetalCmd,
        cardGroupCmd,
        cardNotificationsCmd,
        changePinCodeCmd,
        creditDocsCmd,
        creditPaymentCmd,
        creditReferencesCmd,
        debitFromDepositCmd,
        depositAgreementCmd,
        earlyRepaymentCmd,
        limitsCmd,
        lockCardCmd,
        mortgageInsuranceCmd,
        paymentOrTransferCmd,
        paymentScheduleCmd,
        productCloseCmd,
        renameCmd,
        reopenCardCmd,
        replenishCmd,
        requisitesCardCmd,
        requisitesCmd,
        restrictionsCmd,
        salaryOrPensionTransferRedirectCmd,
        saleMetalCmd,
        statementCmd,
        threeDSecureCmd,
        virtualCardCmd,
        virtualCvvCmd,
        virtualPinCmd,
        withdrawCmd,
        applicationOfOpeningAccountCmd
      ],
      deps: [
        AboutCommand,
        AdditionalCardCommand,
        ArrestCommand,
        B2pCommand,
        BuyMetalCommand,
        CardGroupCommand,
        CardNotificationsCommand,
        ChangePinCodeCommand,
        CreditDocsCommand,
        CreditPaymentCommand,
        CreditReferencesCommand,
        DebitFromDepositCommand,
        DepositAgreementCommand,
        EarlyRepaymentCommand,
        LimitsCommand,
        LockCardCommand,
        MortgageInsuranceCommand,
        PaymentOrTransferCommand,
        PaymentScheduleCommand,
        ProductCloseCommand,
        RenameCommand,
        ReopenCardCommand,
        ReplenishCommand,
        RequisitesCardCommand,
        RequisitesCommand,
        RestrictionsCommand,
        SalaryOrPensionTransferRedirectCommand,
        SaleMetalCommand,
        StatementCommand,
        ThreeDSecureCommand,
        VirtualCardCommand,
        VirtualCvvCommand,
        VirtualPinCommand,
        WithdrawCommand,
        ApplicationOfOpeningAccountCommand
      ]
    }
  ]
})
export class CoreCommandsModule {}

/**
 *     // Restriction commands
    HideAccountCommand,
    SetSpendingLimitCommand,
    HideStatementsCommand,
    DisableParameterChangesCommand,
    RestrictAccountCommand
 */
